import endpoints from 'constants/endpoints';
import {
  GET_RECONCILIATION_FORMS,
  SET_RECONCILIATION_FORMS_LOADING
} from 'constants/types';
import { safePostCall } from 'helpers/firebaseSetup';
import { reconciliationFormFactory } from 'helpers/factory/reconciliationFormFactory';

/**
 * Backend sometimes returns invalid JSON: multiple CRM objects glued as `{...},{...}`
 * without an outer array. Normalize into a value JSON.parse understands.
 */
function parseZohoPortalJsonOutput(output) {
  const raw = String(output ?? '').trim();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    // Glue pattern: `{ "a":1 },{ "b":2 }` → `[ { "a":1 },{ "b":2 } ]`
    if (raw.startsWith('{') && !raw.startsWith('[')) {
      try {
        return JSON.parse(`[${raw}]`);
      } catch {
        // Glue pattern without commas: `{...}{...}` → insert `},{`
        try {
          return JSON.parse(`[${raw.replace(/\}\s*\{/g, '},{')}]`);
        } catch (e2) {
          console.error('reconciliationForms: unable to parse Zoho output', e2);
          return null;
        }
      }
    }
    console.error('reconciliationForms: JSON.parse failed for output');
    return null;
  }
}

/** Zoho may return one record, { data: [...] }, or a plain array. */
function recordsArrayFromZohoOutput(parsed) {
  if (parsed == null) return [];
  if (Array.isArray(parsed)) return parsed;

  if (Array.isArray(parsed.reconciliationForms)) {
    return parsed.reconciliationForms;
  }
  if (Array.isArray(parsed.records)) return parsed.records;

  if (parsed.data != null) {
    if (Array.isArray(parsed.data)) return parsed.data;
    if (typeof parsed.data === 'object' && parsed.data.id != null) {
      return [parsed.data];
    }
  }

  if (
    typeof parsed === 'object' &&
    parsed.id != null &&
    (parsed.Name !== undefined ||
      parsed.Email !== undefined ||
      parsed.Active !== undefined)
  ) {
    return [parsed];
  }

  return [];
}

function parseSupportContacts(parsed) {
  if (!parsed || typeof parsed !== 'object') return null;
  const raw =
    parsed.contacts ||
    parsed.supportContacts ||
    parsed.Support_Contacts ||
    parsed.Portal_Contacts;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out = [];
  for (const c of raw) {
    if (!c || typeof c !== 'object') continue;
    const phone = c.phone || c.Phone || c.tel || c.Tel;
    const email = c.email || c.Email;
    const name = c.name || c.Name || c.role || c.Role || '';
    if (phone) {
      out.push({
        type: 'phone',
        name: String(name || '').trim(),
        tel: String(phone).replace(/\s/g, ''),
        display: String(c.displayPhone || c.display || c.Display || phone)
      });
    } else if (email) {
      out.push({
        type: 'email',
        name: String(name || email),
        email: String(email)
      });
    }
  }
  return out.length ? out : null;
}

/**
 * Sunshine only: Zoho Reconciliation_Forms via SafePortalProxy.
 * Output may be one CRM record, { data: [ ... ] }, or { reconciliationForms: [...] }.
 * Optional `contacts` array on the root for the contact section.
 */
export const getReconciliationForms =
  (setLocalLoader = () => {}, cancelTokenSource = {}) =>
  async (dispatch, getState) => {
    if (!endpoints.zoho.getReconciliationForms) {
      dispatch({
        type: GET_RECONCILIATION_FORMS,
        payload: { records: [], contacts: null }
      });
      setLocalLoader(false);
      return;
    }

    setLocalLoader(true);
    dispatch({ type: SET_RECONCILIATION_FORMS_LOADING, payload: true });

    const zohoId = getState().user.zohoData.id;

    safePostCall(
      endpoints.awsProxy.safePost,
      {
        zohoId,
        endpoint: endpoints.zoho.getReconciliationForms,
        isZohoRequest: true
      },
      { cancelToken: cancelTokenSource.token }
    )
      .then(res => {
        if (!res?.data?.details?.output) {
          dispatch({
            type: GET_RECONCILIATION_FORMS,
            payload: { records: [], contacts: null }
          });
          setLocalLoader(false);
          return;
        }
        try {
          const parsed = parseZohoPortalJsonOutput(res.data.details.output);
          if (parsed == null) {
            dispatch({
              type: GET_RECONCILIATION_FORMS,
              payload: { records: [], contacts: null }
            });
          } else {
            const raw = recordsArrayFromZohoOutput(parsed);
            const records = raw.map(reconciliationFormFactory);
            const contacts = parseSupportContacts(parsed);
            dispatch({
              type: GET_RECONCILIATION_FORMS,
              payload: { records, contacts }
            });
          }
        } catch (e) {
          console.error(e);
          dispatch({
            type: GET_RECONCILIATION_FORMS,
            payload: { records: [], contacts: null }
          });
        }
        setLocalLoader(false);
      })
      .catch(error => {
        console.error(error);
        if (error?.message !== 'API Call Canceled') {
          dispatch({
            type: GET_RECONCILIATION_FORMS,
            payload: { records: [], contacts: null }
          });
          setLocalLoader(false);
        } else {
          dispatch({ type: SET_RECONCILIATION_FORMS_LOADING, payload: false });
        }
      });
  };
